'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';

// One splash per browser tab session, on the homepage only (see the effect below).
const STORAGE_KEY = 'ms_loader_seen';

// "Mohamed Saber" as two words, rendered as real text in the site's Inter font.
const MOHAMED = 'Mohamed'.split('');
const SABER = 'Saber'.split('');

interface MainLoaderProps {
    /** Used only to gate the splash per language so it shows once per locale per session. */
    locale?: string;
}

export default function MainLoader({ locale = 'en' }: MainLoaderProps) {
    // Start hidden. We only mount the splash after confirming it hasn't been shown
    // this session — that way a repeat visit renders nothing (no static flash) and a
    // fresh visit mounts the letters, then the [visible] effect animates them in.
    const [visible, setVisible] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const logoRef = useRef<HTMLDivElement>(null);
    const lettersRef = useRef<HTMLSpanElement[]>([]);

    const addLetter = (el: HTMLSpanElement | null) => {
        if (el && !lettersRef.current.includes(el)) lettersRef.current.push(el);
    };

    const pathname = usePathname();

    // Decide whether to show the splash at all. It is a brand flourish for people who open the
    // homepage, so it must NOT delay content for anyone else:
    //   - deep links (a search result landing on an article, project or tool) never see it,
    //   - automated browsers (crawlers, Lighthouse, tests) never see it,
    //   - it plays at most once per tab session, whichever page the session started on.
    useEffect(() => {
        const alreadySeen = sessionStorage.getItem(STORAGE_KEY) !== null;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isHome = pathname === `/${locale}` || pathname === `/${locale}/`;
        const automated = navigator.webdriver === true;
        // Any first page of the session counts as "seen", so opening the homepage later does not replay it.
        sessionStorage.setItem(STORAGE_KEY, locale);
        if (alreadySeen || reduced || automated || !isHome) return;
        // Reading sessionStorage is only possible after mount, so this can't be derived during render.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setVisible(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- evaluated once, on first mount only
    }, []);

    // Run the animation once the splash is mounted (refs are now attached).
    useEffect(() => {
        if (!visible) return;
        lettersRef.current = lettersRef.current.filter(Boolean);

        const ctx = gsap.context(() => {
            gsap.set(lettersRef.current, { opacity: 0, y: 14 });
            gsap.set(logoRef.current, { opacity: 0, scale: 0.7 });

            gsap.timeline({ onComplete: () => sessionStorage.setItem(STORAGE_KEY, locale) })
                .to(logoRef.current, { opacity: 1, scale: 1, duration: 0.4, ease: 'back.out(1.7)' })
                .to(lettersRef.current, { opacity: 1, y: 0, duration: 0.18, stagger: 0.04, ease: 'back.out(1.4)' }, '-=0.1')
                // Short hold, then fade the whole overlay — keep this brief so it doesn't hurt LCP.
                .to(wrapRef.current, { opacity: 0, duration: 0.45, ease: 'power1.in' }, '+=0.5')
                .add(() => setVisible(false));
        }, wrapRef);

        return () => ctx.revert();
    }, [visible, locale]);

    if (!visible) return null;

    return (
        <div
            ref={wrapRef}
            aria-hidden="true"
            className="fixed inset-0 z-9999 flex items-center justify-center overflow-hidden bg-(--bg)"
            dir="ltr"
        >
            <div className="flex flex-col justify-center items-center gap-4 sm:gap-5">
                {/* Logo */}
                <div ref={logoRef} className="shrink-0">
                    <Image src="/logo.png" alt="Mohamed Saber" width={120} height={48} className="h-8 sm:h-11 w-auto" />
                </div>

                {/* Wordmark — Inter Bold text. Uses --text so it inverts with the
                    theme: dark on the light theme, light on the dark theme. */}
                <div className="flex items-baseline gap-2.5 sm:gap-3.5 text-(--text) font-sans font-bold tracking-tight text-2xl sm:text-4xl select-none">
                    <span className="flex">
                        {MOHAMED.map((letter, i) => (
                            <span key={`m-${i}`} ref={addLetter} className="inline-block">{letter}</span>
                        ))}
                    </span>
                    <span className="flex text-primary-500">
                        {SABER.map((letter, i) => (
                            <span key={`s-${i}`} ref={addLetter} className="inline-block">{letter}</span>
                        ))}
                    </span>
                </div>
            </div>
        </div>
    );
}
